.media-minimal-skin .media-menu {
  --media-menu-transition-duration: 250ms;
  --media-menu-max-height: calc(var(--media-spacing) * 56);
  --media-menu-padding: calc(var(--media-spacing) * 1);
  --media-menu-border-radius: calc(var(--media-spacing) * 2.5);
  --media-menu-item-border-radius: calc(var(--media-menu-border-radius) - var(--media-menu-padding));
  box-sizing: border-box;
  min-width: max-content;
  max-width: var(--media-menu-available-width, none);
  max-height: min(var(--media-menu-available-height, var(--media-menu-max-height)), var(--media-menu-max-height));
  padding: var(--media-menu-padding);
  overflow: auto;
  overscroll-behavior: none;
  background-color: var(--media-popover-background-color);
  border-radius: var(--media-menu-border-radius);
  box-shadow:
    0 0 0 1px var(--media-popover-border-color),
    0 4px 6px -1px oklch(0 0 0 / 0.1),
    0 2px 4px -2px oklch(0 0 0 / 0.1);
  backdrop-filter: var(--media-popover-backdrop-filter);

  @media (prefers-reduced-motion: reduce) {
    --media-menu-transition-duration: 0ms;
  }

  & > .media-menu__panel {
    --media-menu-content-enter-translate: 100%;

    position: absolute;
    inset-inline: 0;
    top: 0;
    z-index: 10;
    max-height: inherit;
    padding: var(--media-menu-padding);
    overflow: auto;
    overscroll-behavior: none;
    outline: none;
    translate: 0 0;
    transition-timing-function: ease-out;
    transition-duration: var(--media-menu-transition-duration);
    transition-property: translate, filter;

    &:where([data-starting-style], [data-ending-style]) {
      overflow: hidden;
      pointer-events: none;
      filter: blur(8px);
      translate: var(--media-menu-content-enter-translate) 0;
    }

    &:dir(rtl):where([data-starting-style], [data-ending-style]) {
      --media-menu-content-enter-translate: -100%;
    }
  }

  .media-menu__separator {
    margin: calc(var(--media-spacing) * 1) 0;
    border-bottom: 1px solid oklch(1 0 0 / 0.1);
  }

  .media-menu__content,
  .media-menu__group {
    anchor-scope: --menu-item-highlight-anchor;
    display: flex;
    flex-direction: column;
    gap: calc(var(--media-spacing) * 0.5);

    @supports (top: anchor(top)) {
      &::before {
        position: absolute;
        position-anchor: --menu-item-highlight-anchor;
        inset: anchor(inside);
        /* Firefox treats the moving highlight as a content shift and re-scrolls
           the menu while hovering. Exclude it from scroll anchoring. */
        overflow-anchor: none;
        pointer-events: none;
        content: "";
        background-color: var(--media-accent-background-color);
        border-radius: var(--media-menu-item-border-radius);
        transition: inset 100ms ease-in-out;
      }

      &:has([data-highlighted=""])::before {
        transition-duration: 0ms;
      }
    }
  }

  .media-menu__item,
  .media-menu__back {
    position: relative;
    display: flex;
    gap: calc(var(--media-spacing) * 1.5);
    align-items: center;
    padding: calc(var(--media-spacing) * 1.5) calc(var(--media-spacing) * 2);
    text-align: start;
    white-space: nowrap;
    text-shadow: 0 1px 0 var(--media-shadow-current-color);
    cursor: pointer;
    user-select: none;
    outline: 2px solid transparent;
    outline-offset: -2px;
    border-radius: var(--media-menu-item-border-radius);
    transition: background-color, color;
    transition-timing-function: ease-in-out;
    transition-duration: 100ms;

    .media-icon {
      flex-shrink: 0;
      color: oklch(from currentColor l c h / 0.5);
      filter: drop-shadow(0 1px 0 var(--media-shadow-current-color));
    }

    &:focus-visible {
      outline-color: var(--media-focus-ring-color);
      outline-offset: 2px;
    }

    &:hover,
    &[data-highlighted] {
      color: var(--media-internal-accent-text-color);
      background-color: var(--media-accent-background-color);

      .media-icon {
        color: inherit;
      }
    }

    @supports (top: anchor(top)) {
      transition-duration: 50ms;

      &:hover,
      &[data-highlighted] {
        transition-duration: 200ms;
      }
    }
  }

  .media-menu__indicator {
    flex-shrink: 0;
    margin-inline: auto calc(var(--media-spacing) * -1);
    opacity: 0;
  }

  .media-menu__item {
    justify-content: space-between;
    font-variant-numeric: tabular-nums;
    color: inherit;

    &[aria-disabled="true"] {
      pointer-events: none;
      cursor: not-allowed;
      opacity: 0.5;
    }

    &[aria-checked="true"] .media-menu__indicator {
      opacity: 1;
    }

    &[data-availability="unavailable"],
    &[data-availability="unsupported"] {
      display: none;
    }

    &[data-highlighted] {
      @supports (top: anchor(top)) {
        anchor-name: --menu-item-highlight-anchor;
        background-color: transparent;
      }
    }
  }

  .media-menu__tier {
    padding-inline-start: calc(var(--media-spacing) * 0.5);
    padding-top: 1px;
    font-size: var(--media-font-size-tiny);
    font-weight: 600;
    line-height: 1;
    color: oklch(from currentColor l c h / 0.7);
  }

  .media-menu__back {
    width: 100%;
    margin-bottom: calc(var(--media-spacing) * 0.5);
  }

  .media-menu__hint {
    display: inline-flex;
    gap: calc(var(--media-spacing) * 1);
    align-items: center;
    min-width: 0;
    padding-inline-start: calc(var(--media-spacing) * 2);
    margin-inline-start: auto;
    color: oklch(from currentColor l c h / 0.65);
  }

  .media-menu__hint-label {
    max-width: calc(var(--media-spacing) * 24);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .media-menu__chevron {
    width: calc(var(--media-spacing) * 3.5);
    height: calc(var(--media-spacing) * 3.5);
  }

  /* Settings menu */
  &.media-menu--settings {
    width: var(--media-menu-width);
    min-width: calc(var(--media-spacing) * 44);
    height: var(--media-menu-height);
    overflow: hidden;
    /* Only the menu size changes between panels. */
    transition:
      var(--media-popup-transition),
      width var(--media-popup-transition-timing-function) var(--media-menu-transition-duration),
      height var(--media-popup-transition-timing-function) var(--media-menu-transition-duration);

    & > .media-menu__content {
      --media-menu-content-exit-translate: -100%;

      translate: 0 0;
      transition:
        translate var(--media-menu-transition-duration) ease-out,
        filter var(--media-menu-transition-duration) ease-out;

      &:dir(rtl) {
        --media-menu-content-exit-translate: 100%;
      }
    }

    & > .media-menu__content[data-child-open] {
      filter: blur(8px);
      translate: var(--media-menu-content-exit-translate) 0;
    }

    /* WebKit can restart the parent Content transition while the
       anchor-positioned highlight remains active. */
    & > .media-menu__content[data-child-open]::before,
    &:has(> .media-menu__panel[data-ending-style]) > .media-menu__content::before {
      display: none;
    }

    /* Don't transition size on open/close. */
    &[data-starting-style],
    &[data-ending-style] {
      transition: var(--media-popup-transition);
    }
  }
}
